import { createContext, useContext, useState, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';

const ToastContext = createContext(null);

function SlideTransition(props) {
  return <Slide {...props} direction='down' />;
}

export function ToastProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);

  const showToast = useCallback((message, severity = 'info', duration = 4000) => {
    const id = Date.now();
    setQueue((prev) => [...prev, { id, message, severity, duration }]);
  }, []);

  const toast = {
    success: (msg, duration) => showToast(msg, 'success', duration),
    error: (msg, duration) => showToast(msg, 'error', duration),
    warning: (msg, duration) => showToast(msg, 'warning', duration),
    info: (msg, duration) => showToast(msg, 'info', duration)
  };

  const processQueue = useCallback(() => {
    if (queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    }
  }, [queue]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setCurrent(null);
    processQueue();
  };

  if (current === null && queue.length > 0) {
    processQueue();
  }

  const severityStyles = {
    success: {
      background: 'linear-gradient(135deg, #1a4731, #1e6e45)',
      border: '1px solid rgba(46, 160, 67, 0.4)',
      color: '#fff'
    },
    error: {
      background: 'linear-gradient(135deg, #4a1a1a, #7a1e1e)',
      border: '1px solid rgba(226, 0, 27, 0.4)',
      color: '#fff'
    },
    warning: {
      background: 'linear-gradient(135deg, #4a3a0a, #7a5e10)',
      border: '1px solid rgba(237, 167, 0, 0.4)',
      color: '#fff'
    },
    info: {
      background: 'linear-gradient(135deg, #0d2d4a, #1a4f7a)',
      border: '1px solid rgba(26, 115, 232, 0.4)',
      color: '#fff'
    }
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Snackbar
        key={current?.id}
        open={open}
        autoHideDuration={current?.duration || 4000}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ top: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } }}
      >
        <Alert
          onClose={handleClose}
          severity={current?.severity || 'info'}
          variant='filled'
          sx={{
            minWidth: 300,
            maxWidth: 480,
            borderRadius: '12px',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            fontSize: '0.875rem',
            fontWeight: 500,
            ...(severityStyles[current?.severity] || severityStyles.info),
            '& .MuiAlert-icon': { color: '#fff', opacity: 0.9 },
            '& .MuiAlert-action': { color: 'rgba(255,255,255,0.7)' }
          }}
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export default ToastContext;
